import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import TuneRoundedIcon from '@material-ui/icons/TuneRounded';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import { Link, useHistory, useLocation } from 'react-router-dom';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import AnalyticsDateRangePicker from '../components/AnalyticsDateRangePicker';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useRouteShopId } from 'features/shell/context/useRouteShopId';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import { ROUTES } from 'app/routes/routeMap';
import { Links } from 'containers/AppLayout/menu/linkBuilder';
import {
  actions as analyticsActions,
  loadAnalyticsQueryDetail,
  selectQueryDetail,
  selectTimeframe,
} from 'slices/AnalyticsSlice';
import {
  runMerchandisingPreview,
} from 'features/merchandising/state/merchandisingSlice';
import { rulesForQuery, useQueryRuleIndex } from '../components/useQueryRuleIndex';
import { keaPalette } from 'styles/designTokens';
import styles from './QueryDetailBridge.module.scss';

const METRICS = ['searches', 'clicks', 'ctr', 'noActions'];
const CHART_COLORS = {
  searches: { stroke: keaPalette.primary, fill: keaPalette.primary },
  clicks: { stroke: '#3776c9', fill: '#79a9e8' },
  ctr: { stroke: '#7656b5', fill: '#aa8cdd' },
  noActions: { stroke: '#b95f21', fill: '#e4a36f' },
};
const PAGE_SIZE = 8;
const CHART_MARGIN = { top: 14, right: 20, left: 0, bottom: 4 };
const TOOLTIP_STYLE = {
  border: '1px solid #dce8e3',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(30, 34, 72, 0.1)',
};
const TOOLTIP_LABEL_STYLE = { color: '#667085', fontWeight: 600 };

const formatInteger = (value) => new Intl.NumberFormat().format(Number(value || 0));
const formatPercent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;
const formatStatus = (status) => {
  const value = String(status || 'draft').replace(/_/g, ' ');
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const metricValue = (metric, value) => (
  metric === 'ctr' ? formatPercent(value) : formatInteger(value)
);

const QueryDetailBridge = ({ match }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const t = useLocalizedMessage();
  const routeShopId = useRouteShopId();
  const { currentShop } = useSelector((state) => state.app);
  const detail = useSelector(selectQueryDetail);
  const timeframe = useSelector(selectTimeframe);
  const { capabilities = {}, productProfile } = useShellAppContext();
  const query = String(match.params.query || '').trim();
  const queryRuleIndex = useQueryRuleIndex();
  const matchingRules = rulesForQuery(queryRuleIndex, query);
  const [metric, setMetric] = useState('searches');
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    setPage(1);
    setPreview(null);
    setPreviewError(null);
  }, [query]);

  useEffect(() => {
    if (!currentShop || !query) return;
    dispatch(loadAnalyticsQueryDetail({ shopId: currentShop, query }));
  }, [currentShop, dispatch, query, timeframe]);

  useEffect(() => {
    if (!query || !capabilities.merchandising_preview_v1) return undefined;
    let active = true;
    setPreviewLoading(true);
    setPreviewError(null);
    dispatch(runMerchandisingPreview({
      workspace: 'query',
      payload: {
        query,
        filters: {},
        ranges: [],
        sort: 'relevance',
        page,
        page_size: PAGE_SIZE,
      },
    })).then((action) => {
      if (!active) return;
      if (runMerchandisingPreview.fulfilled.match(action)) {
        setPreview(action.payload.response?.preview || null);
      } else {
        setPreviewError(action.payload?.message || 'analytics.query.results.error');
      }
      setPreviewLoading(false);
    });
    return () => { active = false; };
  }, [capabilities.merchandising_preview_v1, dispatch, page, query]);

  const chartData = useMemo(() => (detail.curve || []).map((row) => ({
    ...row,
    ctr: Number(row.ctr || 0) * 100,
    label: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
      .format(new Date(`${row.date}T12:00:00`)),
  })), [detail.curve]);

  const products = preview?.proposed?.products || [];
  const relatedQueries = detail.relatedQueries || [];
  const relatedProducts = detail.relatedProducts || [];
  const totalProducts = Number(preview?.proposed?.total || 0);
  const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));
  const rules = [...matchingRules.active, ...matchingRules.other];
  const primaryRule = matchingRules.active[0] || matchingRules.other[0] || null;
  const chartMetricLabel = t(`analytics.query.metric.${metric}`);
  const chartColors = CHART_COLORS[metric];
  const returnPath = `${location.pathname}${location.search}`;
  const productCatalogEnabled = productProfile?.id === 'cloud';
  const rulesBasePath = ROUTES.merchandisingQueryRules(routeShopId);
  const backSource = useMemo(() => {
    const candidate = new URLSearchParams(location.search).get('return_to');
    const reports = [
      {
        path: Links.searchQueryPopular(routeShopId),
        title: 'search.analytics.queryPopular.header',
      },
      {
        path: Links.searchQueryNoResult(routeShopId),
        title: 'search.analytics.queryNoResult.header',
      },
      {
        path: Links.searchLostOpportunities(routeShopId),
        title: 'search.analytics.lostOpportunities.header',
      },
    ];
    const report = reports.find(({ path }) => (
      candidate === path || candidate?.startsWith(`${path}?`)
    ));
    return report ? { ...report, path: candidate } : reports[0];
  }, [location.search, routeShopId]);
  const formatAxisValue = (value) => (
    metric === 'ctr' ? `${Number(value).toFixed(0)}%` : formatInteger(value)
  );

  const rulePath = (ruleId) => {
    const params = new URLSearchParams({ return_to: returnPath });
    return `${rulesBasePath}/${ruleId}?${params.toString()}`;
  };

  const createRule = () => {
    const params = new URLSearchParams({
      query,
      return_to: returnPath,
    });
    history.push(`${rulesBasePath}/new?${params.toString()}`);
  };

  const modifyResults = () => {
    if (primaryRule) history.push(rulePath(primaryRule.id));
    else createRule();
  };

  const handleTimeframeChange = (range) => dispatch(analyticsActions.setTimeframe(range));

  return (
    <SearchFeaturePageShell
      pageId="analyticQueryDetail"
      actions={(
        <AnalyticsDateRangePicker
          timeframe={timeframe}
          onSubmitRange={handleTimeframeChange}
          isFetching={detail.isFetching}
          localizeMessage={t}
        />
      )}
    >
      <div className={styles.page}>
        <Link className={styles.backLink} to={backSource.path}>
          <ArrowBackRoundedIcon aria-hidden="true" />
          {t(backSource.title)}
        </Link>

        <section className={styles.queryHeader}>
          <div>
            <span className={styles.eyebrow}>{t('analytics.table.query')}</span>
            <h2>“{query}”</h2>
          </div>
        </section>

        <section className={styles.metrics} aria-label={t('analytics.query.metrics')}>
          {[
            ['searches', detail.metrics.searches],
            ['clicks', detail.metrics.clicks],
            ['ctr', detail.metrics.ctr],
            ['noActions', detail.metrics.noActions],
          ].map(([key, value]) => {
            const content = (
              <>
                <span>{t(`analytics.query.metric.${key}`)}</span>
                <strong>{key === 'ctr' ? formatPercent(value) : formatInteger(value)}</strong>
              </>
            );
            return METRICS.includes(key) ? (
              <button
                key={key}
                aria-pressed={metric === key}
                className={styles.metricCard}
                data-metric={key}
                type="button"
                onClick={() => setMetric(key)}
              >
                {content}
              </button>
            ) : (
              <article key={key} className={styles.metricCard} data-metric={key}>
                {content}
              </article>
            );
          })}
          {detail.isFetching && <div className={styles.metricsLoading} aria-label={t('common.loading')} />}
        </section>

        <section className={styles.chartCard}>
          <header className={styles.cardHeader}>
            <h3>{t('analytics.query.performance')}</h3>
          </header>
          <figure
            className={styles.chart}
            aria-label={t('analytics.query.chart.summary', { metric: chartMetricLabel, query })}
          >
            <figcaption className={styles.srOnly}>
              {t('analytics.query.chart.summary', { metric: chartMetricLabel, query })}
            </figcaption>
            {detail.error && <div className={styles.inlineError} role="alert">{detail.error}</div>}
            {!detail.isFetching && !detail.error && chartData.length === 0 && (
              <div className={styles.empty}>{t('analytics.query.performance.empty')}</div>
            )}
            {chartData.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id={`queryMetricFill-${metric}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors.fill} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={chartColors.fill} stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e7eeeb" vertical={false} strokeDasharray="2 7" />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    minTickGap={32}
                    tickLine={false}
                    tick={{ fill: '#77808f', fontSize: 12 }}
                  />
                  <YAxis
                    allowDecimals={metric === 'ctr'}
                    axisLine={false}
                    tickFormatter={formatAxisValue}
                    tickLine={false}
                    tick={{ fill: '#77808f', fontSize: 12 }}
                    width={48}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value) => metricValue(metric, metric === 'ctr' ? value / 100 : value)}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                  />
                  <Area
                    type="monotone"
                    dataKey={metric}
                    stroke={chartColors.stroke}
                    strokeWidth={2.5}
                    fill={`url(#queryMetricFill-${metric})`}
                    animationDuration={420}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
            <table className={styles.srOnly}>
              <caption>{t('analytics.query.chart.summary', { metric: chartMetricLabel, query })}</caption>
              <thead><tr><th>{t('analytics.query.chart.date')}</th><th>{chartMetricLabel}</th></tr></thead>
              <tbody>
                {chartData.map((row) => (
                  <tr key={row.date}><td>{row.label}</td><td>{metricValue(metric, metric === 'ctr' ? row[metric] / 100 : row[metric])}</td></tr>
                ))}
              </tbody>
            </table>
          </figure>
        </section>

        <section className={styles.relatedGrid} aria-label={t('analytics.query.relatedInsights')}>
          <article className={styles.relatedCard}>
            <header className={styles.cardHeader}>
              <h3>{t('search.analytics.queryDetail.related.header')}</h3>
              {detail.relatedQueriesSampled && (
                <span className={styles.sampleBadge}>
                  {t('analytics.query.relatedQueries.sampled', {
                    count: formatInteger(detail.relatedQueriesSampleSize),
                  })}
                </span>
              )}
            </header>
            {detail.relatedQueriesUnavailable ? (
              <div className={styles.relatedUnavailable} role="status">
                {t('analytics.query.relatedQueries.unavailable')}
              </div>
            ) : relatedQueries.length > 0 ? (
              <ul className={styles.relatedList}>
                {relatedQueries.map(row => (
                  <li key={row.query}>
                    <Link to={Links.searchQueryDetail(routeShopId, row.query)}>{row.query}</Link>
                    <span>{formatInteger(row.count)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={styles.relatedEmpty}>{t('analytics.query.relatedQueries.empty')}</div>
            )}
          </article>
          <article className={styles.relatedCard}>
            <header className={styles.cardHeader}>
              <h3>{t('search.analytics.queryDetail.related.products.header')}</h3>
            </header>
            {detail.relatedProductsUnavailable ? (
              <div className={styles.relatedUnavailable} role="status">
                {t('analytics.query.relatedProducts.unavailable')}
              </div>
            ) : relatedProducts.length > 0 ? (
              <ul className={styles.relatedList}>
                {relatedProducts.map(row => (
                  <li key={row.productId}>
                    {productCatalogEnabled && row.productId != null ? (
                      <Link
                        className={styles.relatedProductName}
                        to={Links.storeProductDetail(routeShopId, row.productId)}
                      >
                        {row.name || `#${row.productId}`}
                      </Link>
                    ) : (
                      <span className={styles.relatedProductName}>{row.name || `#${row.productId}`}</span>
                    )}
                    <span>{formatInteger(row.count)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={styles.relatedEmpty}>{t('analytics.query.relatedProducts.empty')}</div>
            )}
          </article>
        </section>

        <section className={styles.rulesCard}>
          <header className={styles.rulesHeader}>
            <span className={styles.rulesIcon}><TuneRoundedIcon aria-hidden="true" /></span>
            <div>
              <h3>{t('analytics.query.rules.title')}</h3>
              <span>
                {matchingRules.active.length > 0
                  ? t('analytics.query.rules.count', { count: matchingRules.active.length })
                  : t('analytics.query.rules.none')}
              </span>
            </div>
          </header>
          <div className={styles.ruleList}>
            {rules.map((rule) => (
              <Link
                className={styles.ruleLink}
                key={rule.id}
                to={rulePath(rule.id)}
              >
                <span className={styles.ruleName}>{rule.name || (rule.members || [])[0]?.query || query}</span>
                <span className={styles.ruleStatus}>{formatStatus(rule.status)}</span>
                <ArrowForwardRoundedIcon aria-hidden="true" />
              </Link>
            ))}
            <button className={styles.createRuleButton} type="button" onClick={createRule}>
              <AddRoundedIcon aria-hidden="true" />
              {t('analytics.query.rules.create')}
            </button>
          </div>
        </section>

        <section className={styles.resultsCard}>
          <header className={styles.resultsHeader}>
            <div>
              <h3>{t('analytics.query.results.title')}</h3>
              {!previewLoading && preview && <span>{t('analytics.query.results.count', { count: totalProducts })}</span>}
            </div>
            <button className={styles.modifyResultsButton} type="button" onClick={modifyResults}>
              <TuneRoundedIcon aria-hidden="true" />
              {t('analytics.query.rules.edit')}
            </button>
          </header>
          <div className={styles.productGrid} aria-busy={previewLoading}>
            {products.map((product, index) => {
              const content = (
                <>
                  <div className={styles.productImage}>
                    {(product.image_url || product.thumbnail_url || product.thumbnail || product.picture)
                      ? <img src={product.image_url || product.thumbnail_url || product.thumbnail || product.picture} alt="" />
                      : <span aria-hidden="true">{index + 1}</span>}
                  </div>
                  <div className={styles.productBody}>
                    <strong>{product.name}</strong>
                    <span>{product.reference || product.vendorCode || `#${product.id}`}</span>
                    {product.category_name && <small>{product.category_name}</small>}
                  </div>
                </>
              );
              return productCatalogEnabled && product.id != null ? (
                <Link
                  className={styles.productCard}
                  key={product.id}
                  to={Links.storeProductDetail(routeShopId, product.id)}
                >
                  {content}
                </Link>
              ) : (
                <article className={`${styles.productCard} ${styles.productCardDisabled}`} key={product.id}>
                  {content}
                </article>
              );
            })}
            {previewLoading && (
              <div className={styles.previewLoading} aria-label={t('analytics.query.results.loading')}>
                <span />
              </div>
            )}
          </div>
          {previewError && (
            <div className={styles.inlineError} role="alert">
              {previewError === 'analytics.query.results.error' ? t(previewError) : previewError}
            </div>
          )}
          {!previewLoading && !previewError && products.length === 0 && (
            <div className={styles.empty}>{t('analytics.query.results.empty')}</div>
          )}
          {!previewLoading && totalPages > 1 && (
            <nav className={styles.pagination} aria-label={t('analytics.query.results.pagination')}>
              <button type="button" disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                <ArrowBackRoundedIcon aria-hidden="true" />
                {t('common.previous')}
              </button>
              <span>{t('analytics.query.results.page', { page, total: totalPages })}</span>
              <button type="button" disabled={page === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>
                {t('common.next')}
                <ArrowForwardRoundedIcon aria-hidden="true" />
              </button>
            </nav>
          )}
        </section>
      </div>
    </SearchFeaturePageShell>
  );
};

QueryDetailBridge.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ query: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
};

export default QueryDetailBridge;
